import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import { SectionHeader } from '../components/ui/SectionHeader';
import { EmptyState } from '../components/ui/EmptyState';
import {
  Bell,
  BellRing,
  CalendarClock,
  AlertTriangle,
  TrendingDown,
  DollarSign,
  ChevronRight,
  Send,
  ListPlus,
  CheckCircle,
  X,
} from 'lucide-react';

// ---- Types ----

type AlertType = 'cd_maturity' | 'delinquency' | 'lifecycle_change' | 'balance_threshold';
type AlertSeverity = 'high' | 'medium' | 'low';

interface AlertRecord {
  id: string;
  type: AlertType;
  title: string;
  memberName: string;
  memberId: string;
  detail: string;
  severity: AlertSeverity;
  createdAt: string;
}

// ---- Mock data ----

const MOCK_ALERTS: AlertRecord[] = [
  {
    id: 'alt-001',
    type: 'cd_maturity',
    title: 'CD Maturity in 14 Days',
    memberName: 'Sarah Chen',
    memberId: 'cccc0001-0001-4000-8000-cccc00000001',
    detail: '$50,000 Certificate matures 2026-03-31 — member has not responded to rollover offer.',
    severity: 'medium',
    createdAt: new Date(Date.now() - 2 * 3600000).toISOString(),
  },
  {
    id: 'alt-002',
    type: 'delinquency',
    title: '30-Day Delinquency — Auto Loan',
    memberName: 'James Rivera',
    memberId: 'cccc0001-0002-4000-8000-cccc00000002',
    detail: 'Loan #LN-2024-0044 past due 31 days. Balance $28,900. Adverse action notice pending.',
    severity: 'high',
    createdAt: new Date(Date.now() - 5 * 3600000).toISOString(),
  },
  {
    id: 'alt-003',
    type: 'lifecycle_change',
    title: 'Member Lifecycle Change',
    memberName: 'Derek Fontaine',
    memberId: 'cccc0001-0003-4000-8000-cccc00000003',
    detail: 'AI flagged transition from growth to at_risk based on 3 consecutive minimum payments.',
    severity: 'medium',
    createdAt: new Date(Date.now() - 24 * 3600000).toISOString(),
  },
  {
    id: 'alt-004',
    type: 'balance_threshold',
    title: 'Savings Below Threshold',
    memberName: 'Linda Okafor',
    memberId: 'cccc0001-0005-4000-8000-cccc00000005',
    detail: 'Checking account balance dropped below $500 — potential overdraft risk.',
    severity: 'low',
    createdAt: new Date(Date.now() - 48 * 3600000).toISOString(),
  },
  {
    id: 'alt-005',
    type: 'cd_maturity',
    title: 'CD Maturity in 7 Days',
    memberName: 'Angela Park',
    memberId: 'cccc0001-0004-4000-8000-cccc00000004',
    detail: '$120,000 Certificate matures 2026-03-24 — high-value member, priority outreach recommended.',
    severity: 'high',
    createdAt: new Date(Date.now() - 30 * 60000).toISOString(),
  },
];

const MOCK_TEMPLATES = [
  { id: 'tpl-001', name: 'CD Rollover Offer', channel: 'email' as const },
  { id: 'tpl-002', name: 'Payment Reminder', channel: 'email' as const },
  { id: 'tpl-003', name: 'Account Review Follow-Up', channel: 'email' as const },
  { id: 'tpl-004', name: 'Welcome to New Benefits', channel: 'email' as const },
  { id: 'tpl-005', name: 'Delinquency Notice', channel: 'sms' as const },
];

// ---- Helpers ----

function fmtRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ---- Alert type config ----

interface AlertTypeMeta {
  Icon: typeof Bell;
}

const TYPE_MAP: Record<AlertType, AlertTypeMeta> = {
  cd_maturity: { Icon: CalendarClock },
  delinquency: { Icon: AlertTriangle },
  lifecycle_change: { Icon: TrendingDown },
  balance_threshold: { Icon: DollarSign },
};

// ---- Severity config ----

interface SeverityMeta {
  badgeCls: string;
  borderCls: string;
  label: string;
}

const SEVERITY_MAP: Record<AlertSeverity, SeverityMeta> = {
  high: {
    badgeCls: 'bg-fail-soft text-fail',
    borderCls: 'border-t-[3px] border-t-fail',
    label: 'High',
  },
  medium: {
    badgeCls: 'bg-warn-soft text-warn',
    borderCls: 'border-t-[3px] border-t-warn-fill',
    label: 'Medium',
  },
  low: {
    badgeCls: 'bg-info-soft text-info',
    borderCls: 'border-t-[3px] border-t-info',
    label: 'Low',
  },
};

// ---- TemplatePickerList ----

interface TemplatePickerListProps {
  templates: Array<{ id: string; name: string; channel: string }>;
  selectedId: string | null;
  onSelect: (id: string) => void;
}

function TemplatePickerList({ templates, selectedId, onSelect }: TemplatePickerListProps): React.ReactElement {
  if (templates.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-sm font-semibold text-ink-soft">No templates available</p>
        <p className="text-xs text-ink-muted mt-1">
          Templates will appear here once they are approved. Contact your administrator.
        </p>
      </div>
    );
  }

  return (
    <div className="max-h-[360px] overflow-y-auto flex flex-col gap-1">
      {templates.map((tpl) => (
        <button
          key={tpl.id}
          type="button"
          onClick={() => onSelect(tpl.id)}
          className={cn(
            'w-full text-left p-3 rounded-lg flex items-center justify-between cursor-pointer transition-colors',
            selectedId === tpl.id
              ? 'bg-brand/5 border border-brand'
              : 'hover:bg-bg border border-transparent',
          )}
        >
          <span className="text-sm font-medium text-ink">{tpl.name}</span>
          {tpl.channel === 'email' ? (
            <span className="bg-info-soft text-info text-xs px-1.5 py-0.5 rounded">email</span>
          ) : (
            <span className="bg-running-soft text-running-ink text-xs px-1.5 py-0.5 rounded">sms</span>
          )}
        </button>
      ))}
    </div>
  );
}

// ---- WorkQueueForm ----

interface WorkQueueFormProps {
  title: string;
  onTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  dueDate: string;
  onDueDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

function WorkQueueForm({ title, onTitleChange, dueDate, onDueDateChange }: WorkQueueFormProps): React.ReactElement {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col">
        <label className="text-xs font-medium text-ink-soft mb-1">Task title</label>
        <input
          className="w-full border border-line rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
          value={title}
          onChange={onTitleChange}
        />
      </div>
      <div className="flex flex-col">
        <label className="text-xs font-medium text-ink-soft mb-1">Due date (optional)</label>
        <input
          type="date"
          className="w-full border border-line rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
          value={dueDate}
          onChange={onDueDateChange}
        />
      </div>
    </div>
  );
}

// ---- AlertActionDrawer ----

interface AlertActionDrawerProps {
  isOpen: boolean;
  alert: AlertRecord | null;
  mode: 'template' | 'work_queue' | null;
  onClose: () => void;
  onActionComplete: (alertId: string, action: 'template_sent' | 'queued') => void;
  drawerLoading: boolean;
  selectedTemplateId: string | null;
  onSelectTemplate: (id: string) => void;
  taskTitle: string;
  onTaskTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  taskDueDate: string;
  onTaskDueDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  templates: Array<{ id: string; name: string; channel: string }>;
}

function AlertActionDrawer({
  isOpen,
  alert,
  mode,
  onClose,
  onActionComplete,
  drawerLoading,
  selectedTemplateId,
  onSelectTemplate,
  taskTitle,
  onTaskTitleChange,
  taskDueDate,
  onTaskDueDateChange,
  templates,
}: AlertActionDrawerProps): React.ReactElement {
  const drawerTitle = mode === 'template' ? 'Send Template' : 'Add to Work Queue';
  const submitLabel = mode === 'template' ? 'Send' : 'Add to Queue';

  const isSubmitDisabled =
    drawerLoading ||
    (mode === 'template' && selectedTemplateId === null);

  const handleSubmit = () => {
    if (alert === null || mode === null) return;
    const action: 'template_sent' | 'queued' = mode === 'template' ? 'template_sent' : 'queued';
    onActionComplete(alert.id, action);
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/20"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={drawerTitle}
        className={cn(
          'fixed inset-y-0 right-0 z-40 w-96 bg-white border-l border-line shadow-xl flex flex-col',
          'transform transition-transform duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        {/* Header */}
        <div className="h-14 flex items-center justify-between px-4 border-b border-line flex-shrink-0">
          <div>
            <p className="text-sm font-semibold text-ink">{drawerTitle}</p>
            {alert !== null && (
              <p className="text-xs text-ink-muted">For: {alert.memberName}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close action drawer"
            className="text-ink-muted hover:text-ink-soft transition-colors p-1"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
          {mode === 'template' && (
            <TemplatePickerList
              templates={templates}
              selectedId={selectedTemplateId}
              onSelect={onSelectTemplate}
            />
          )}
          {mode === 'work_queue' && (
            <WorkQueueForm
              title={taskTitle}
              onTitleChange={onTaskTitleChange}
              dueDate={taskDueDate}
              onDueDateChange={onTaskDueDateChange}
            />
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-line p-4 flex gap-2 justify-end flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-ink-soft hover:text-ink transition-colors rounded-lg"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            className={cn(
              'px-4 py-2 text-sm font-semibold text-on-brand bg-brand rounded-lg transition-colors',
              isSubmitDisabled
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-brand/90',
            )}
          >
            {drawerLoading ? 'Sending…' : submitLabel}
          </button>
        </div>
      </div>
    </>
  );
}

// ---- AlertCard ----

interface AlertCardProps {
  alert: AlertRecord;
  onViewMember: () => void;
  onSendTemplate: () => void;
  onAddToQueue: () => void;
  completedAction?: 'template_sent' | 'queued';
}

function AlertCard({ alert, onViewMember, onSendTemplate, onAddToQueue, completedAction }: AlertCardProps): React.ReactElement {
  const typeMeta = TYPE_MAP[alert.type];
  const severityMeta = SEVERITY_MAP[alert.severity];
  const { Icon: TypeIcon } = typeMeta;

  return (
    <div
      role="article"
      aria-label={`${alert.title} — ${alert.memberName}`}
      className={cn(
        'bg-white rounded-xl border border-sunken shadow-sm p-4',
        severityMeta.borderCls,
      )}
    >
      <div className="flex flex-wrap items-start gap-2 mb-2">
        {/* Icon + title */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <TypeIcon className="h-4 w-4 text-ink-muted flex-shrink-0" aria-hidden />
          <span className="font-semibold text-ink text-sm">{alert.title}</span>
        </div>

        {/* Severity badge + View Member button */}
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
              severityMeta.badgeCls,
            )}
          >
            <BellRing className="h-3 w-3" aria-hidden />
            {severityMeta.label}
          </span>

          {/* Zone B — View Member CTA */}
          <button
            type="button"
            onClick={onViewMember}
            className="inline-flex items-center gap-1 text-xs font-medium text-brand-dark hover:text-brand-dark/80 transition-colors"
            aria-label={`View member profile for ${alert.memberName}`}
          >
            View Member
            <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* Zone A — Member name clickable deep link */}
      <button
        type="button"
        onClick={onViewMember}
        className="text-xs font-semibold text-brand-dark underline cursor-pointer hover:text-brand-dark/80 transition-colors mb-1"
        aria-label={`View member profile for ${alert.memberName}`}
        tabIndex={0}
      >
        {alert.memberName}
      </button>

      {/* Detail */}
      <p className="text-sm text-ink-soft leading-relaxed">{alert.detail}</p>

      {/* Created at */}
      <p className="text-xs text-ink-muted mt-2">{fmtRelative(alert.createdAt)}</p>

      {/* Zone B — Action buttons or confirmation badge */}
      <div className="flex items-center gap-1 mt-2">
        {completedAction === 'template_sent' ? (
          <span className="inline-flex items-center gap-1 bg-pass-soft text-pass text-xs px-2 py-0.5 rounded-full">
            <CheckCircle className="h-4 w-4" />
            Template sent
          </span>
        ) : completedAction === 'queued' ? (
          <span className="inline-flex items-center gap-1 bg-brand-tint text-brand-dark text-xs px-2 py-0.5 rounded-full">
            <CheckCircle className="h-4 w-4" />
            Added to queue
          </span>
        ) : (
          <>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onSendTemplate(); }}
              className="text-ink-muted hover:text-brand-dark hover:bg-bg rounded-lg px-3 py-2 text-xs font-medium flex items-center gap-1.5 transition-colors min-h-[44px]"
            >
              <Send className="h-3.5 w-3.5" />
              Send Template
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onAddToQueue(); }}
              className="text-ink-muted hover:text-brand-dark hover:bg-bg rounded-lg px-3 py-2 text-xs font-medium flex items-center gap-1.5 transition-colors min-h-[44px]"
            >
              <ListPlus className="h-3.5 w-3.5" />
              Add to Queue
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ---- AlertsPage ----

export function AlertsPage(): React.ReactElement {
  const [showEmpty, setShowEmpty] = useState(false);
  const navigate = useNavigate();

  // Drawer state
  const [activeActionAlert, setActiveActionAlert] = useState<AlertRecord | null>(null);
  const [actionMode, setActionMode] = useState<'template' | 'work_queue' | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [completedActions, setCompletedActions] = useState<Map<string, 'template_sent' | 'queued'>>(new Map());
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [templates, setTemplates] = useState<Array<{ id: string; name: string; channel: string }>>([]);

  const isEmpty = showEmpty || MOCK_ALERTS.length === 0;

  // Toast auto-dismiss
  React.useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const openDrawer = (alert: AlertRecord, mode: 'template' | 'work_queue') => {
    setActiveActionAlert(alert);
    setActionMode(mode);
    setSelectedTemplateId(null);
    setTaskTitle(`Alert: ${alert.title} — ${alert.memberName}`);
    setTaskDueDate('');
    // Attempt to fetch templates when opening template mode
    if (mode === 'template') {
      fetch('/api/templates', {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token') ?? ''}` },
      })
        .then(r => r.ok ? r.json() : Promise.reject(r))
        .then((data: Array<{ id: string; name: string; channel: string }>) => setTemplates(data))
        .catch(() => setTemplates(MOCK_TEMPLATES)); // dev-mode fallback
    }
  };

  const closeDrawer = () => {
    setActiveActionAlert(null);
    setActionMode(null);
    setDrawerLoading(false);
  };

  const handleActionComplete = async (alertId: string, action: 'template_sent' | 'queued') => {
    setDrawerLoading(true);
    try {
      if (action === 'queued' && activeActionAlert !== null) {
        await fetch('/api/work-queue/tasks', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token') ?? ''}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            title: taskTitle,
            description: activeActionAlert.detail,
            memberId: activeActionAlert.memberId,
            ...(taskDueDate ? { dueDate: taskDueDate } : {}),
          }),
        }).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); });
        setCompletedActions(prev => new Map(prev).set(alertId, 'queued'));
        setToast('Task added to work queue.');
      } else if (action === 'template_sent' && activeActionAlert !== null && selectedTemplateId !== null) {
        const res = await fetch(`/api/templates/${selectedTemplateId}/send`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token') ?? ''}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            memberId: activeActionAlert.memberId,
            channel: 'email',
          }),
        });
        if (res.status === 403) {
          setToast(`Consent required — ${activeActionAlert.memberName} has not opted in to receive this type of message.`);
          setDrawerLoading(false);
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setCompletedActions(prev => new Map(prev).set(alertId, 'template_sent'));
        setToast(`Template sent to ${activeActionAlert.memberName}.`);
      }
    } catch {
      // Dev-mode: simulate success
      setCompletedActions(prev => new Map(prev).set(alertId, action));
      const msg = action === 'queued'
        ? 'Task added to work queue. (dev mode)'
        : `Template sent to ${activeActionAlert?.memberName ?? 'member'}. (dev mode)`;
      setToast(msg);
    }
    setDrawerLoading(false);
    closeDrawer();
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <SectionHeader title="Alerts" />
          <button
            type="button"
            onClick={() => setShowEmpty(!showEmpty)}
            className="text-xs text-ink-muted hover:text-ink-soft ml-2"
          >
            [Toggle empty]
          </button>
        </div>
        <p className="text-sm text-ink-muted mt-1">
          CD maturity notices, delinquency flags, lifecycle change events, balance threshold alerts
        </p>
      </div>

      {/* Alert cards or empty state */}
      {isEmpty ? (
        <EmptyState
          icon={Bell}
          headline="No active alerts"
          body="CD maturity notices, delinquency flags, and lifecycle events will appear as they are detected."
        />
      ) : (
        <div className="flex flex-col gap-3">
          {MOCK_ALERTS.map((alert) => (
            <AlertCard
              key={alert.id}
              alert={alert}
              onViewMember={() => navigate(`/members/${alert.memberId}`)}
              onSendTemplate={() => openDrawer(alert, 'template')}
              onAddToQueue={() => openDrawer(alert, 'work_queue')}
              completedAction={completedActions.get(alert.id)}
            />
          ))}
        </div>
      )}

      {/* Action drawer */}
      <AlertActionDrawer
        isOpen={activeActionAlert !== null}
        alert={activeActionAlert}
        mode={actionMode}
        onClose={closeDrawer}
        onActionComplete={(alertId, action) => { void handleActionComplete(alertId, action); }}
        drawerLoading={drawerLoading}
        selectedTemplateId={selectedTemplateId}
        onSelectTemplate={setSelectedTemplateId}
        taskTitle={taskTitle}
        onTaskTitleChange={(e) => setTaskTitle(e.target.value)}
        taskDueDate={taskDueDate}
        onTaskDueDateChange={(e) => setTaskDueDate(e.target.value)}
        templates={templates}
      />

      {/* Toast */}
      {toast !== null && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-pass text-white px-4 py-3 rounded-xl shadow-lg text-sm">
          <CheckCircle className="h-4 w-4" />
          {toast}
        </div>
      )}
    </div>
  );
}
